import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import UploadInstance, { UPLOAD_STATES } from 'dpl/shared/utils/UploadInstance';

const HOOK_TYPES = {
  SINGLE_ERROR: 'singleError',
  SINGLE_SUCCESS: 'singleSuccess',
  ALL_ERROR: 'allError',
  ALL_SUCCESS: 'allSuccess'
};

const { IDLE, UPLOADING, SUCCESS, ERROR } = UPLOAD_STATES;

export default function withUploadCapabilities(WrappedComponent) {
  class UploadComponent extends Component {
    static displayName = `UploadComponent(${
      WrappedComponent.displayName || WrappedComponent.name
    })`;

    static propTypes = {
      directUploadURL: PropTypes.string.isRequired
    };

    state = { uploads: {} };

    hooks = {
      singleError: {},
      singleSuccess: {},
      allError: {},
      allSuccess: {}
    };

    registerHook = hookType => (inputName, fn) => {
      this.hooks[hookType][inputName] = fn;
      return fn;
    };

    getHook(inputName, hookType) {
      return this.hooks[hookType][inputName] || (() => null);
    }

    clearUploads = inputName => {
      this.state.uploads[inputName].forEach(upload => {
        if (upload.file?.type?.includes('video/')) {
          URL.revokeObjectURL(upload.dataUrl);
        }
      });
      this.setState({ uploads: { ...this.state.uploads, [inputName]: [] } });
    };

    clearAllUploads = () => this.setState({ uploads: {} });

    removeUpload = (inputName, signedId) =>
      this.setState({
        uploads: {
          ...this.state.uploads,
          [inputName]: this.state.uploads[inputName].filter(upload => {
            if (upload.blob.signed_id !== signedId) {
              return true;
            }

            if (upload.file?.type?.includes('video/')) {
              URL.revokeObjectURL(upload.dataUrl);
            }
            return false;
          })
        }
      });

    handleChange = e => {
      const {
        target: { files, name: inputName }
      } = e;

      if (!files || !files.length) {
        return;
      }

      const { directUploadURL } = this.props;

      const { uploads } = this.state;

      const newInputUploads = [...files].map(file => {
        const upload = new UploadInstance({ file, url: directUploadURL });
        upload
          .start()
          .then(() =>
            this.getHook(inputName, HOOK_TYPES.SINGLE_SUCCESS)(upload)
          )
          .catch(err => {
            if (!CONFIG.isTest) {
              /* eslint-disable-next-line no-console */
              console.error(err);
            }
            return this.getHook(inputName, HOOK_TYPES.SINGLE_ERROR)(
              err,
              upload
            );
          });
        return upload;
      });

      this.setState({
        uploads: {
          ...uploads,
          [inputName]: uploads[inputName]
            ? [...uploads[inputName], ...newInputUploads]
            : newInputUploads
        }
      });

      Promise.all(newInputUploads.map(({ promise }) => promise))
        .then(() =>
          this.getHook(inputName, HOOK_TYPES.ALL_SUCCESS)(newInputUploads)
        )
        .catch(err => {
          if (!CONFIG.isTest) {
            /* eslint-disable-next-line no-console */
            console.error(err);
          }
          return this.getHook(inputName, HOOK_TYPES.ALL_ERROR)(
            err,
            newInputUploads.filter(({ error }) => Boolean(error))
          );
        });
    };

    getUploadsByStatus(status) {
      const { uploads } = this.state;

      return Object.entries(uploads).reduce(
        (acc, [inputName, inputUploads]) => ({
          ...acc,
          [inputName]: inputUploads.filter(
            ({ status: uploadStatus }) => uploadStatus === status
          )
        }),
        {}
      );
    }

    render() {
      const { uploads } = this.state;

      return (
        <WrappedComponent
          {...this.props}
          clearAllUploads={this.clearAllUploads}
          clearUploads={this.clearUploads}
          removeUpload={this.removeUpload}
          uploads={uploads}
          allIdleUploads={this.getUploadsByStatus(IDLE)}
          allUploadingUploads={this.getUploadsByStatus(UPLOADING)}
          allSuccessUploads={this.getUploadsByStatus(SUCCESS)}
          allErrorUploads={this.getUploadsByStatus(ERROR)}
          getUploadsByStatus={this.getUploadsByStatus}
          handleUploadInputChange={this.handleChange}
          handleSingleUploadError={this.registerHook(HOOK_TYPES.SINGLE_ERROR)}
          handleSingleUploadSuccess={this.registerHook(
            HOOK_TYPES.SINGLE_SUCCESS
          )}
          handleAllUploadError={this.registerHook(HOOK_TYPES.ALL_ERROR)}
          handleAllUploadSuccess={this.registerHook(HOOK_TYPES.ALL_SUCCESS)}
        />
      );
    }
  }

  return connect(({ server: { directUploadURL } }) => ({ directUploadURL }))(
    UploadComponent
  );
}
